import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    alpha
} from '@mui/material';
import {
    Warning as WarningIcon,
    Delete as DeleteIcon,
    Close as CloseIcon
} from '@mui/icons-material';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const DeleteConfirmationDialog = () => {
    const dispatch = useDispatch();

    // Redux state
    const isOpen = useSelector(selectors.isDeleteDialogOpenSelector);
    const menuToDelete = useSelector(selectors.menuToDeleteSelector);
    const menus = useSelector(selectors.menusSelector);
    const loading = useSelector(selectors.loadingSelector);

    // Find the menu details
    const menuDetails = menus.find((m) => m.menuId === menuToDelete);

    const handleClose = () => {
        dispatch(actions.closeDeleteDialog());
    };

    const handleConfirmDelete = () => {
        if (menuToDelete) {
            dispatch(actions.deleteMenu(menuToDelete));
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderTop: 3,
                    borderColor: 'error.main'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="error" />
                <Typography variant="h6" component="span">
                    Confirm Deletion
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    This action cannot be undone!
                </Alert>

                <Typography variant="body1" gutterBottom>
                    Are you sure you want to delete this menu?
                </Typography>

                {menuDetails && (
                    <Box
                        sx={(theme) => ({
                            mt: 2,
                            p: 2,
                            bgcolor: alpha(theme.palette.divider, 0.06),
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: theme.palette.divider
                        })}
                    >
                        <Typography variant="body2" color="text.secondary">
                            <strong>Menu ID:</strong> {menuDetails.menuId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Name:</strong> {menuDetails.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Code:</strong> {menuDetails.code}
                        </Typography>
                        {menuDetails.permissions && menuDetails.permissions.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Permissions:</strong>{' '}
                                {menuDetails.permissions.length} permission(s)
                            </Typography>
                        )}
                    </Box>
                )}

                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    ⚠ Warning: Deleting this menu may affect the application navigation and
                    user access permissions.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={handleClose}
                    startIcon={<CloseIcon />}
                    disabled={loading}
                    variant="outlined"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirmDelete}
                    variant="contained"
                    color="error"
                    startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
                    disabled={loading}
                >
                    Delete Menu
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmationDialog;
